/**
 * Manual pages definitions
 * Static menu item definitions for User Manual
 */

import * as HeroIcons from '@heroicons/react/24/outline';
import {
  Cog6ToothIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import {
  Cog6ToothIcon as Cog6ToothIconSolid,
  BriefcaseIcon as BriefcaseIconSolid,
} from '@heroicons/react/24/solid';
import { ROUTES } from '@/constants/routes';
import type { MenuItem } from '@/types/userManual';

export const getAdminPages = (t: (key: string) => string): MenuItem[] => [
  {
    icon: <HeroIcons.UserGroupIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.UserGroupIcon className="w-5 h-5" />,
    label: 'Användare',
    path: ROUTES.ADMIN_USERS,
    featureCode: 'ADMIN_USERS',
    description: 'Hantera användare, behörigheter och lösenord',
  },
  {
    icon: <HeroIcons.MapIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.MapIcon className="w-5 h-5" />,
    label: t('tripAnalysis.title'),
    path: '/admin/trip-analysis',
    featureCode: 'ADMIN_TRIP_ANALYSIS',
    description: 'Jämför registrerade trippar mot fordonets bin-fil för att identifiera avvikelser',
  },
  {
    icon: <Cog6ToothIcon className="w-5 h-5" />,
    iconSolid: <Cog6ToothIconSolid className="w-5 h-5" />,
    label: t('roles.title'),
    path: ROUTES.ADMIN_ROLES,
    featureCode: 'ADMIN_ROLES',
    description: 'Skapa och hantera roller med specifika behörigheter',
  },
  {
    icon: <HeroIcons.BuildingOffice2Icon className="w-5 h-5" />,
    iconSolid: <HeroIcons.BuildingOffice2Icon className="w-5 h-5" />,
    label: t('organizations.title'),
    path: ROUTES.ADMIN_ORGANIZATIONS,
    featureCode: 'ADMIN_ORGANIZATIONS',
    description: 'Hantera organisationshierarki och kundstrukturer',
  },
  {
    icon: <HeroIcons.LanguageIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.LanguageIcon className="w-5 h-5" />,
    label: t('translations.title'),
    path: ROUTES.ADMIN_TRANSLATIONS,
    featureCode: 'ADMIN_TRANSLATIONS',
    description: 'Hantera översättningar för svenska och engelska',
  },
  {
    icon: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    label: 'Geofences',
    path: ROUTES.ADMIN_GEOFENCES,
    featureCode: 'ADMIN_GEOFENCES',
    description: 'Skapa geografiska zoner för larm vid in-/utpassering',
  },
  {
    icon: <HeroIcons.BellAlertIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.BellAlertIcon className="w-5 h-5" />,
    label: 'Geofence-händelser',
    path: ROUTES.ADMIN_GEOFENCE_EVENTS,
    featureCode: 'ADMIN_GEOFENCE_EVENTS',
    description: 'Visa logg över alla geofence-larm',
  },
  {
    icon: <HeroIcons.ArrowDownTrayIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ArrowDownTrayIcon className="w-5 h-5" />,
    label: 'Import',
    path: ROUTES.ADMIN_IMPORTS,
    featureCode: 'ADMIN_IMPORTS',
    description: 'Importera fordon och data från Excel/CSV',
  },
  {
    icon: <HeroIcons.CubeIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CubeIcon className="w-5 h-5" />,
    label: 'Paket',
    path: ROUTES.ADMIN_PACKAGES,
    featureCode: 'ADMIN_PACKAGES',
    description: 'Hantera tjänstepaket och prisplaner',
  },
  {
    icon: <HeroIcons.DevicePhoneMobileIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.DevicePhoneMobileIcon className="w-5 h-5" />,
    label: 'Enheter',
    path: ROUTES.ADMIN_DEVICES,
    featureCode: 'ADMIN_DEVICES',
    description: 'Hantera GPS-enheter och deras koppling till fordon',
  },
  {
    icon: <HeroIcons.WrenchScrewdriverIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.WrenchScrewdriverIcon className="w-5 h-5" />,
    label: 'Enhetshantering',
    path: ROUTES.ADMIN_DEVICE_MANAGEMENT,
    featureCode: 'ADMIN_DEVICE_MANAGEMENT',
    description: 'Avancerad enhetsadministration och konfiguration',
  },
  {
    icon: <HeroIcons.SignalIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.SignalIcon className="w-5 h-5" />,
    label: 'SIM-kort',
    path: ROUTES.ADMIN_SIM_CARDS,
    featureCode: 'ADMIN_SIM_CARDS',
    description: 'Hantera SIM-kort och mobilabonnemang (Tele2/Jasper)',
  },
  {
    icon: <HeroIcons.ChatBubbleLeftRightIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ChatBubbleLeftRightIcon className="w-5 h-5" />,
    label: 'Support',
    path: ROUTES.ADMIN_SUPPORT,
    featureCode: 'ADMIN_SUPPORT',
    description: 'Supportärenden och kundkommunikation',
  },
];

export const getOtherPages = (): MenuItem[] => [
  {
    icon: <HeroIcons.MapIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.MapIcon className="w-5 h-5" />,
    label: 'Realtidsövervakning',
    path: ROUTES.LIVE_TRACKING,
    featureCode: 'LIVE_TRACKING',
    description: 'Visa alla fordon i realtid på karta med trädvy för organisationer',
  },
  {
    icon: <HeroIcons.FireIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.FireIcon className="w-5 h-5" />,
    label: 'Heatmap',
    path: ROUTES.ANALYSIS_HEATMAP,
    featureCode: 'ANALYSIS_HEATMAP',
    description: 'Visualisera var fordon kör mest med värmekarta',
  },
];

export const getReportPages = (): MenuItem[] => [
  {
    icon: <HeroIcons.ClipboardDocumentListIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ClipboardDocumentListIcon className="w-5 h-5" />,
    label: 'Trippar',
    path: ROUTES.REPORTS_TRIPS,
    featureCode: 'REPORTS_TRIPS',
    description: 'Lista alla resor med start/slut, distans och tid',
  },
  {
    icon: <HeroIcons.DocumentTextIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.DocumentTextIcon className="w-5 h-5" />,
    label: 'Körjournal',
    path: ROUTES.REPORTS_DRIVING_LOG,
    featureCode: 'REPORTS_DRIVING_LOG',
    description: 'Körjournal enligt Skatteverkets krav med mätarställning, CSV-export',
  },
  {
    icon: <HeroIcons.ArrowTrendingUpIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ArrowTrendingUpIcon className="w-5 h-5" />,
    label: 'Ekokörning',
    path: ROUTES.REPORTS_ECO_DRIVING,
    featureCode: 'REPORTS_ECO_DRIVING',
    description: 'Analysera körstil: acceleration, inbromsningar, tomgång, coasting',
  },
  {
    icon: <HeroIcons.ExclamationTriangleIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ExclamationTriangleIcon className="w-5 h-5" />,
    label: 'Hastighetsöverträdelser',
    path: ROUTES.REPORTS_SPEED_COMPLIANCE,
    featureCode: 'REPORTS_SPEED_COMPLIANCE',
    description: 'Visa var och när hastighetsgränser överskridits',
  },
  {
    icon: <HeroIcons.BoltIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.BoltIcon className="w-5 h-5" />,
    label: 'Energiförbrukning',
    path: ROUTES.REPORTS_ENERGY_CONSUMPTION,
    featureCode: 'REPORTS_ENERGY_CONSUMPTION',
    description: 'Bränsle- och elförbrukning per fordon och period',
  },
  {
    icon: <HeroIcons.WrenchIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.WrenchIcon className="w-5 h-5" />,
    label: 'Fordonsslitage',
    path: ROUTES.REPORTS_VEHICLE_WEAR,
    featureCode: 'REPORTS_VEHICLE_WEAR',
    description: 'Analysera körbeteende som påverkar fordonsslitage (mervärde 120 000 kr)',
  },
  {
    icon: <HeroIcons.ClockIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ClockIcon className="w-5 h-5" />,
    label: 'Underutnyttjade fordon',
    path: ROUTES.REPORTS_UNDERUTILIZED,
    featureCode: 'REPORTS_UNDERUTILIZED',
    description: 'Identifiera fordon med låg användningsgrad (mervärde 100 000 kr)',
  },
  {
    icon: <HeroIcons.GlobeEuropeAfricaIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.GlobeEuropeAfricaIcon className="w-5 h-5" />,
    label: 'CO2-utsläpp',
    path: ROUTES.REPORTS_CO2,
    featureCode: 'REPORTS_CO2',
    description: 'Beräkna och rapportera koldioxidutsläpp',
  },
  {
    icon: <HeroIcons.CalendarDaysIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CalendarDaysIcon className="w-5 h-5" />,
    label: 'Bokningsstatistik',
    path: ROUTES.REPORTS_BOOKING_STATS,
    featureCode: 'REPORTS_BOOKING_STATS',
    description: 'Volym, beläggning, request-flöde och trend för bokningar',
  },
  {
    icon: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    label: 'Geofence-zoner',
    path: ROUTES.REPORTS_GEOFENCE_ZONES,
    featureCode: 'REPORTS_GEOFENCE_ZONES',
    description: 'Rapport över geofence-händelser och tid i zoner',
  },
  {
    icon: <HeroIcons.BeakerIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.BeakerIcon className="w-5 h-5" />,
    label: 'Bränsleanalys',
    path: ROUTES.REPORTS_FUEL_ANALYSIS,
    featureCode: 'REPORTS_FUEL_ANALYSIS',
    description: 'Analysera bränslekostnader, förbrukning och kr/mil från importerade transaktioner',
  },
  {
    icon: <HeroIcons.CurrencyDollarIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CurrencyDollarIcon className="w-5 h-5" />,
    label: 'Flottkostnadsanalys (TCO)',
    path: ROUTES.REPORTS_FLEET_TCO,
    featureCode: 'REPORTS_FLEET_TCO',
    description: 'Total ägandekostnad per fordon: bränsle, service, leasing, försäkring och skatt (kr/km)',
  },
  {
    icon: <HeroIcons.PresentationChartLineIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.PresentationChartLineIcon className="w-5 h-5" />,
    label: 'Ledningsrapport',
    path: ROUTES.REPORTS_EXECUTIVE,
    featureCode: 'REPORTS_EXECUTIVE',
    description: 'Övergripande KPI:er för hela flottan: kostnader, utnyttjandegrad, CO₂ och topp-5 avvikelser',
  },
];

export const getSysadminPages = (): MenuItem[] => [
  {
    icon: <HeroIcons.CircleStackIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CircleStackIcon className="w-5 h-5" />,
    label: 'Data-migrering',
    path: ROUTES.SYSADMIN_DATA_MIGRATION,
    featureCode: 'SYSADMIN_DATA_MIGRATION',
    description: 'Migrera data mellan databaser och format',
  },
  {
    icon: <HeroIcons.CommandLineIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CommandLineIcon className="w-5 h-5" />,
    label: 'Enhetskommandon',
    path: ROUTES.SYSADMIN_DEVICE_COMMANDS,
    featureCode: 'SYSADMIN_DEVICE_COMMANDS',
    description: 'Skicka SMS-kommandon till GPS-enheter',
  },
  {
    icon: <HeroIcons.ClipboardDocumentCheckIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ClipboardDocumentCheckIcon className="w-5 h-5" />,
    label: 'Audit-logg',
    path: ROUTES.SYSADMIN_AUDIT_LOG,
    featureCode: 'SYSADMIN_AUDIT_LOG',
    description: 'Granska alla ändringar i systemet',
  },
  {
    icon: <HeroIcons.DocumentMagnifyingGlassIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.DocumentMagnifyingGlassIcon className="w-5 h-5" />,
    label: 'Generell audit-logg',
    path: ROUTES.SYSADMIN_GENERAL_AUDIT_LOG,
    featureCode: 'SYSADMIN_GENERAL_AUDIT_LOG',
    description: 'Visa alla systemhändelser och användaraktivitet',
  },
  {
    icon: <HeroIcons.ExclamationCircleIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ExclamationCircleIcon className="w-5 h-5" />,
    label: 'Felloggar',
    path: ROUTES.SYSADMIN_ERROR_LOG,
    featureCode: 'SYSADMIN_ERROR_LOG',
    description: 'Visa och analysera systemfel',
  },
  {
    icon: <HeroIcons.AdjustmentsHorizontalIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.AdjustmentsHorizontalIcon className="w-5 h-5" />,
    label: 'ECO-inställningar',
    path: ROUTES.SYSADMIN_ECO_SETTINGS,
    featureCode: 'SYSADMIN_ECO_SETTINGS',
    description: 'Konfigurera tröskelvärden för ekokörningsanalys',
  },
  {
    icon: <HeroIcons.ComputerDesktopIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ComputerDesktopIcon className="w-5 h-5" />,
    label: 'Systemmonitor',
    path: ROUTES.SYSADMIN_SYSTEM_MONITOR,
    featureCode: 'SYSADMIN_SYSTEM_MONITOR',
    description: 'Övervaka systemhälsa, databaser och processer',
  },
  {
    icon: <HeroIcons.ClockIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ClockIcon className="w-5 h-5" />,
    label: 'Långsamma queries',
    path: ROUTES.SYSADMIN_SLOW_QUERIES,
    featureCode: 'SYSADMIN_SLOW_QUERIES',
    description: 'Identifiera och optimera långsamma databasfrågor',
  },
  {
    icon: <HeroIcons.ShieldCheckIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ShieldCheckIcon className="w-5 h-5" />,
    label: 'Certifikatbevakning',
    path: ROUTES.SYSADMIN_CERTIFICATE_MONITOR,
    featureCode: 'SYSADMIN_CERT_MONITOR',
    description: 'Bevaka certifikat som går ut (OIDC/SSO) och hur de förnyas',
  },
  {
    icon: <HeroIcons.LockClosedIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.LockClosedIcon className="w-5 h-5" />,
    label: 'Security',
    path: ROUTES.SYSADMIN_SECURITY,
    featureCode: 'SYSADMIN',
    description: 'Fail2ban-bans, skyddsloggar, frigör IP och safelista',
  },
  {
    icon: <HeroIcons.QueueListIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.QueueListIcon className="w-5 h-5" />,
    label: 'Enhetsköer',
    path: ROUTES.SYSADMIN_DEVICE_QUEUES,
    featureCode: 'SYSADMIN_DEVICE_QUEUES',
    description: 'Visa och hantera väntande kommandon, OBD P&P och Non-OBD till enheter',
  },
  {
    icon: <HeroIcons.ArrowPathIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ArrowPathIcon className="w-5 h-5" />,
    label: 'Data-replay',
    path: ROUTES.SYSADMIN_DATA_REPLAY,
    featureCode: 'SYSADMIN_DATA_REPLAY',
    description: 'Spela om GPS-data för att regenerera trippar',
  },
];

export const getCrmPages = (): MenuItem[] => [
  {
    icon: <BriefcaseIcon className="w-5 h-5" />,
    iconSolid: <BriefcaseIconSolid className="w-5 h-5" />,
    label: 'CRM',
    path: ROUTES.CRM,
    featureCode: 'CRM',
    description: 'Kundhantering och CRM-funktioner',
  },
  {
    icon: <HeroIcons.UserGroupIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.UserGroupIcon className="w-5 h-5" />,
    label: 'Kunder',
    path: ROUTES.CRM_CUSTOMERS,
    featureCode: 'CRM_CUSTOMERS',
    description: 'Kundlista med kontaktpersoner, statistik och kundkort',
  },
  {
    icon: <HeroIcons.PlusCircleIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.PlusCircleIcon className="w-5 h-5" />,
    label: 'Ny kund (Wizard)',
    path: ROUTES.CRM_CUSTOMER_WIZARD,
    featureCode: 'CRM_CUSTOMER_WIZARD',
    description: '5-stegs wizard för att skapa nya kunder (Prospect eller Aktiv)',
  },
  {
    icon: <HeroIcons.CurrencyDollarIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.CurrencyDollarIcon className="w-5 h-5" />,
    label: 'Prislista',
    path: ROUTES.CRM_PRICELIST,
    featureCode: 'CRM_PRICELIST',
    description: 'Kategorier, produkter och prissättning',
  },
  {
    icon: <HeroIcons.FolderIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.FolderIcon className="w-5 h-5" />,
    label: 'Projekt',
    path: ROUTES.CRM_PROJECTS,
    featureCode: 'CRM_PROJECTS',
    description: 'Pågående och planerade projekt',
  },
  {
    icon: <HeroIcons.ChartBarSquareIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ChartBarSquareIcon className="w-5 h-5" />,
    label: 'Säljpipeline',
    path: ROUTES.CRM_PIPELINE,
    featureCode: 'CRM_PIPELINE',
    description: 'Säljcase, kravanalys, affärsvärde och koppling till produktutveckling',
  },
  {
    icon: <HeroIcons.DocumentTextIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.DocumentTextIcon className="w-5 h-5" />,
    label: 'Upphandlingar',
    path: ROUTES.CRM_PROCUREMENTS,
    featureCode: 'CRM_PROCUREMENTS',
    description: 'LOU och anbudsprocesser',
  },
  {
    icon: <HeroIcons.ChartBarIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ChartBarIcon className="w-5 h-5" />,
    label: 'Ekonomisk översikt',
    path: ROUTES.CRM_ECONOMY_OVERVIEW,
    featureCode: 'CRM_ECONOMY',
    description: 'Rullande 12, trender och intäkter',
  },
];

export const getBookingPages = (t: (key: string) => string): MenuItem[] => [
  {
    icon: <HeroIcons.ArrowPathIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.ArrowPathIcon className="w-5 h-5" />,
    label: t('booking.vehicleReplacementTitle'),
    path: '/booking/vehicle-replacement',
    featureCode: 'BOOKING_VEHICLE_REPLACEMENT',
    description: t('modules.vehicle-replacement.description'),
  },
  {
    icon: <HeroIcons.TruckIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.TruckIcon className="w-5 h-5" />,
    label: t('booking.ownCar.formTitle'),
    path: '/booking/own-car-trips',
    featureCode: 'BOOKING_OWN_CAR_TRIP',
    description: t('modules.own-car-trip.description'),
  },
  {
    icon: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    iconSolid: <HeroIcons.MapPinIcon className="w-5 h-5" />,
    label: t('modules.pickup-locations.title'),
    path: '/booking/pickup-locations',
    featureCode: 'BOOKING_PICKUP_LOCATIONS',
    description: t('modules.pickup-locations.description'),
  },
];

// Icon categories for display
export const iconCategories: Record<string, string[]> = {
  navigation: [
    'TruckIcon', 'MapIcon', 'ChartBarIcon', 'UserIcon', 'BookOpenIcon',
    'HomeIcon', 'BuildingOfficeIcon', 'BuildingOffice2Icon', 'MapPinIcon',
    'GlobeAltIcon', 'GlobeAmericasIcon', 'GlobeEuropeAfricaIcon',
  ],
  arrows: [
    'ArrowLeftIcon', 'ArrowRightIcon', 'ArrowUpIcon', 'ArrowDownIcon',
    'ArrowPathIcon', 'ArrowPathRoundedSquareIcon', 'ArrowUturnLeftIcon',
    'ArrowUturnRightIcon', 'ArrowUturnUpIcon', 'ArrowUturnDownIcon',
    'ChevronLeftIcon', 'ChevronRightIcon', 'ChevronUpIcon', 'ChevronDownIcon',
    'ChevronDoubleLeftIcon', 'ChevronDoubleRightIcon', 'ChevronDoubleUpIcon', 'ChevronDoubleDownIcon',
  ],
  actions: [
    'MagnifyingGlassIcon', 'BellIcon', 'MoonIcon', 'SunIcon', 'EyeIcon', 'EyeSlashIcon',
    'PlusIcon', 'MinusIcon', 'XMarkIcon', 'CheckIcon', 'PencilIcon', 'TrashIcon',
    'ShareIcon', 'DownloadIcon', 'UploadIcon', 'ArrowDownTrayIcon', 'ArrowUpTrayIcon',
    'PrinterIcon', 'DocumentIcon', 'DocumentTextIcon', 'DocumentDuplicateIcon',
    'ClipboardIcon', 'ClipboardDocumentIcon', 'ClipboardDocumentCheckIcon',
    'FunnelIcon', 'AdjustmentsHorizontalIcon', 'AdjustmentsVerticalIcon',
    'PlayIcon', 'PauseIcon', 'StopIcon', 'ForwardIcon', 'BackwardIcon',
  ],
  admin: [
    'Cog6ToothIcon', 'Cog8ToothIcon', 'CircleStackIcon', 'ServerIcon',
    'UserGroupIcon', 'UsersIcon', 'UserCircleIcon', 'UserPlusIcon',
    'ShieldCheckIcon', 'ShieldExclamationIcon', 'LockClosedIcon', 'LockOpenIcon',
    'KeyIcon', 'IdentificationIcon', 'CreditCardIcon', 'BanknotesIcon',
    'WrenchScrewdriverIcon', 'WrenchIcon', 'ToolIcon',
  ],
  communication: [
    'ChatBubbleLeftIcon', 'ChatBubbleLeftRightIcon', 'ChatBubbleOvalLeftIcon',
    'EnvelopeIcon', 'EnvelopeOpenIcon', 'PhoneIcon', 'VideoCameraIcon',
    'MegaphoneIcon', 'SpeakerWaveIcon', 'SpeakerXMarkIcon',
  ],
  files: [
    'FolderIcon', 'FolderOpenIcon', 'DocumentIcon', 'DocumentTextIcon',
    'DocumentDuplicateIcon', 'DocumentArrowDownIcon', 'DocumentArrowUpIcon',
    'PhotoIcon', 'FilmIcon', 'MusicalNoteIcon',
  ],
  status: [
    'CheckCircleIcon', 'XCircleIcon', 'ExclamationCircleIcon', 'ExclamationTriangleIcon',
    'InformationCircleIcon', 'QuestionMarkCircleIcon', 'ClockIcon',
    'CheckBadgeIcon', 'XMarkIcon', 'CheckIcon',
  ],
  media: [
    'PhotoIcon', 'CameraIcon', 'VideoCameraIcon', 'FilmIcon',
    'MusicalNoteIcon', 'SpeakerWaveIcon', 'PlayIcon', 'PauseIcon',
  ],
  other: [
    'HeartIcon', 'StarIcon', 'BookmarkIcon', 'TagIcon', 'HashtagIcon',
    'CalendarIcon', 'CalendarDaysIcon', 'ClockIcon', 'StopwatchIcon',
    'ShoppingCartIcon', 'GiftIcon', 'FireIcon', 'LightBulbIcon',
    'BoltIcon', 'Battery0Icon', 'Battery50Icon', 'Battery100Icon',
    'WifiIcon', 'SignalIcon', 'SignalSlashIcon',
    'CubeIcon', 'CubeTransparentIcon', 'PuzzlePieceIcon',
    'FlagIcon', 'TrophyIcon', 'AcademicCapIcon', 'GraduationCapIcon',
    'BriefcaseIcon', 'SuitcaseIcon', 'BuildingLibraryIcon',
    'BeakerIcon', 'FlaskIcon', 'MicroscopeIcon',
    'PaintBrushIcon', 'SwatchIcon', 'ColorSwatchIcon',
    'RocketLaunchIcon', 'SparklesIcon', 'FireIcon',
  ],
};
